import invariant from 'invariant';
import type { MetaDescriptor } from 'react-router';

import { useEmbedSearchWidget } from 'modules/embedSearchWidget/contexts/EmbedSearchWidgetContext';
import type { EmbedSearchWidgetLayoutHandleProps } from 'modules/embedSearchWidget/layouts/layout/EmbedSearchWidgetLayoutHandleProps';
import { listingEmbedApiIsomorphicFetchVolop } from 'modules/embedSearchWidget/listing/volop/api/listingEmbedApiFetchVolop';
import type { embedSearchWidgetViewVolopRoute } from 'modules/embedSearchWidget/routing/embedSearchWidget.routes';
import { EMBED_SEARCH_WIDGET_VOLOP_SOURCES_ALLOWED } from 'modules/embedSearchWidget/volops/search/embedSearchWidgetVolopSearchConstants';
import { SearchVolopsRefinements } from 'modules/search/pages/Volops/components/SearchVolopsRefinements';
import { NotFoundPageLoadable } from 'pages/NotFound/NotFoundPageLoadable';
import type { HorizonAppRouteLoaderArgs } from 'routing/classes/types/AppRouteLoaderArgs';
import type { HorizonAppRouteComponentProps } from 'routing/types/HorizonAppRouteComponentProps';
import type { HorizonRouteMetaArgs } from 'routing/types/HorizonRouteMetaArgs';

import { EmbedSearchWidgetViewVolopPageLoadable } from './EmbedSearchWidgetViewVolopPageLoadable';

export const handle = {
  searchRefinements: (
    <SearchVolopsRefinements
      sourcesAllowed={EMBED_SEARCH_WIDGET_VOLOP_SOURCES_ALLOWED}
    />
  ),
  defaultSearchType: 'VOLOP',
  isSearchPage: false,
  searchFromPageType: 'LISTING',
} satisfies EmbedSearchWidgetLayoutHandleProps;

export async function loader({
  request,
  context,
  params: { embedSearchWidgetId, volopId },
}: HorizonAppRouteLoaderArgs<typeof embedSearchWidgetViewVolopRoute>) {
  try {
    const [volop] = await Promise.all([
      listingEmbedApiIsomorphicFetchVolop({ request, context })(
        embedSearchWidgetId,
        volopId,
      ),
      EmbedSearchWidgetViewVolopPageLoadable.load(),
    ]);

    return { volop };
  } catch {
    return { volop: null };
  }
}

export function meta({
  loaderData: { volop },
}: HorizonRouteMetaArgs<typeof loader>): MetaDescriptor[] {
  return [
    { title: volop && volop.type === 'VOLOP' ? volop.name : 'Not found' },
  ];
}

export default function EmbedSearchWidgetViewVolopHorizonRoute({
  loaderData: { volop },
}: HorizonAppRouteComponentProps<typeof loader>) {
  const { embedSearchWidget } = useEmbedSearchWidget();
  invariant(embedSearchWidget, 'Embed search widget not set.');

  if (!volop || volop.type !== 'VOLOP') return <NotFoundPageLoadable />;

  return (
    <EmbedSearchWidgetViewVolopPageLoadable
      volop={volop}
      embedSearchWidget={embedSearchWidget}
    />
  );
}
